
import moment from "moment";
import { saveAs } from 'file-saver';
import { ActionType, getType } from "typesafe-actions";
import { all, call, put, select, takeLatest } from "redux-saga/effects";
import { evalActions } from "../../actions/entities/eval.actions";
import { AppState } from "../../types/state/AppState";
import { EvalFilter, EvalOrderByColumn } from "../../types/state/entities/EvalState";
import { isRequesting } from "../../utils";
import { RequestState } from "../../constants/request-state";
import { convertToApiCriteria, getDateOptionByRange } from "../../utils/filtering/filter.utils";
import { EvalSearchResult } from "../../types/eval/EvalSearchResult";
import { evalService } from "../../services/eval.service";
import { createFilterActions } from "../../actions/filter.actions";
import { BwicFilterType, TFilter } from "../../types/filters/FilterState";
import { errorActions, searchSecuritiesActions } from "../../actions";
import { collateralManagers, dateFrom, dateTo, identifiers, ratings } from "../../utils/filtering/serializers/serializer.definitions";
import { QueryStringArgumentSerializer } from "../../utils/filtering/serializers/QueryStringArgumentSerializer";
import { queryStringSerializer } from "../../utils/filtering";
import { history } from "../../history";
import { Rating } from "../../types/enums/Rating";
import { Company } from "../../types/amr-pipeline/models/Company";
import { DeserializeCommandHandler } from "../../utils/filtering/serializers/DeserializeCommand";
import { dateRangeFilterOptions } from "../../constants/date-range.filter";

const filterActions = createFilterActions(BwicFilterType.Eval);

function* watchInit(action: ActionType<typeof evalActions.init>) {
    const { sortField, sortDirection } = action.payload;

    const managers: Company[] = yield select((state: AppState) => state.entities.collateralManagers.items);
    const filter: TFilter<BwicFilterType.Eval> = yield select((s: AppState) => s.filters.eval.filter);

    yield put(filterActions.init(filter));

    const locationSearchString = history.location.search;
    const searchString = locationSearchString && locationSearchString.trim();

    if (searchString) {
        let from: Date | undefined | null = undefined;
        let to: Date | undefined | null = undefined;

        let identifiersList: string[] = [];
        let ratingsOptions: Rating[] = [];
        let collateralManagersReferenceNames: string[] = [];

        const serializers: QueryStringArgumentSerializer<any>[] = [
            dateFrom(parsedDate => from = parsedDate),
            dateTo(parsedDate => to = parsedDate),
            identifiers(list => identifiersList = list),
            ratings(parsedRatings => ratingsOptions = parsedRatings.map(r => Rating[r as keyof typeof Rating])),
            collateralManagers(values => collateralManagersReferenceNames = values, managers.map(m => m.referenceName)),
        ];
        queryStringSerializer.deserialize(searchString, serializers);

        const selectedDateOption = getDateOptionByRange(dateRangeFilterOptions.All, from, to);
        const date = {
            shouldExecute: () => !!from || !!to,
            execute: function* () {
                if (selectedDateOption.key !== dateRangeFilterOptions.All.key) {
                    yield put(filterActions.filterDateSelectOption(selectedDateOption, 'date'));
                    yield put(filterActions.filterDateSelectCustomRange({ from, to }, 'date'));
                }
            }
        };

        const identifiersCommand = {
            shouldExecute: () => !!identifiersList.length,
            execute: function* () {
                yield all(identifiersList.map(i => put(searchSecuritiesActions.addSearchItem(i))));
            }
        }

        const rating = {
            execute: function* () {
                yield all(ratingsOptions.map((r: Rating) =>
                    put(filterActions.filterSelectChange(r, 'ratings'))
                ));
            }
        };

        const collateralManagersCommand = {
            shouldExecute: () => !!collateralManagersReferenceNames.length,
            execute: function* () {
                yield all(collateralManagersReferenceNames.map((cm: string) =>
                    put(filterActions.filterSelectChange(cm, 'managers'))
                ));
            }
        }

        yield new DeserializeCommandHandler()
            .addCommand(date)
            .addCommand(identifiersCommand)
            .addCommand(rating)
            .addCommand(collateralManagersCommand)
            .processGenerator()
    } else {
        yield put(filterActions.filterDateSelectOption(
            { ...dateRangeFilterOptions.Today, from: moment().toDate(), to: moment().toDate() },
            'date'));
    }

    yield put(evalActions.request(sortField, sortDirection, true));
}

function* watchRequest(action: ActionType<typeof evalActions.request>) {
    const { sortField, sortDirection, applyFilter } = action.payload;
    const {
        currentPageNumber,
        requestState,
        pageSize
    } = yield select((state: AppState) => state.entities.eval);

    if (applyFilter) {
        yield put(filterActions.applyFilter());
    }

    const filter: EvalFilter = yield select((state: AppState) =>
        state.filters.eval.lastAppliedFilter ?? state.filters.eval.filter);
    const searchSecurities: string[] = yield select((state: AppState) => state.searchSecurities.searchTermItems) ?? [];

    if (isRequesting(requestState)) return;

    yield put(evalActions.requestStateChange(RequestState.request));

    const searchParams = getSearchParams(
        filter,
        searchSecurities,
        sortField,
        sortDirection,
        currentPageNumber,
        pageSize
    );

    try {
        const { result, totalRecordNumber }: EvalSearchResult = yield call(evalService.searchEval, searchParams);
        yield put(evalActions.requestResult(RequestState.success, result, totalRecordNumber));
        yield call(redirectWithFilterArguments, filter, searchSecurities);
    } catch (e) {
        yield put(evalActions.requestStateChange(RequestState.failure));
        yield put(errorActions.criticalError(e));
    }
}

function getSearchParams(
    filter: EvalFilter,
    searchSecurities: string[],
    sortField: EvalOrderByColumn,
    sortDirection: string,
    currentPageNumber?: number,
    pageSize?: number
) {
    const { dateFrom, dateTo } = filter.date.filter.selectedOption
        ? convertToApiCriteria(filter.date.filter.selectedOption, filter.date.filter.options)
        : { dateFrom: undefined, dateTo: undefined };

    return {
        identifiers: searchSecurities,
        dateFrom,
        dateTo,
        ratings: filter.ratings.filter.filter(r => r.selected).map(r => r.value),
        managers: filter.managers.filter.filter(c => c.selected).map(c => c.value),
        orderByColumn: sortField,
        sortOrder: sortDirection,
        page: currentPageNumber || undefined,
        pageSize: pageSize || undefined
    }
}

function* redirectWithFilterArguments(
    filter: EvalFilter,
    isinCusipsAndTickers: string[]) {

    const serializers: QueryStringArgumentSerializer<any>[] = [
        dateFrom(), dateTo(), identifiers(), ratings(), collateralManagers()
    ];

    const dateRange = filter.date.filter.selectedOption
        ? convertToApiCriteria(filter.date.filter.selectedOption, filter.date.filter.options)
        : { dateFrom: undefined, dateTo: undefined };

    const data = {
        [dateFrom().key]: dateRange.dateFrom,
        [dateTo().key]: dateRange.dateTo,
        [ratings().key]: filter.ratings.filter.filter(r => r.selected).map(r => r.value),
        [identifiers().key]: isinCusipsAndTickers,
        [collateralManagers().key]: filter.managers.filter.filter(c => c.selected).map(c => c.value)
    };

    const queryString = queryStringSerializer.serialize(data, serializers);

    if (queryString) {
        yield call(history.replace, history.location.pathname + '?' + queryString);
    } else if (history.location.search) {
        yield call(history.replace, history.location.pathname);
    }
}

function* watchExport(action: ActionType<typeof evalActions.exportData>) {
    const { sortField, sortDirection } = action.payload;
    const {
        currentPageNumber,
        requestState,
        exportRequestState,
        pageSize
    } = yield select((state: AppState) => state.entities.eval);

    const filter: EvalFilter = yield select((state: AppState) =>
        state.filters.eval.lastAppliedFilter);
    const searchSecurities: string[] = yield select((state: AppState) => state.searchSecurities.searchTermItems) ?? [];

    if (!filter || isRequesting(requestState, exportRequestState)) return;

    yield put(evalActions.exportRequestStateChange(RequestState.request));

    const searchParams = getSearchParams(
        filter,
        searchSecurities,
        sortField,
        sortDirection,
        currentPageNumber,
        pageSize
    );

    try {
        const file: { blob: Blob, name: string } = yield call(evalService.exportData, searchParams);
        saveAs(file.blob, file.name);
        yield put(evalActions.exportRequestStateChange(RequestState.success));
    } catch (e) {
        yield put(errorActions.unexpectedError(e));
        yield put(evalActions.exportRequestStateChange(RequestState.failure));
    }
}

export function* watchEval() {
    yield takeLatest(getType(evalActions.init), watchInit);
    yield takeLatest(getType(evalActions.request), watchRequest);
    yield takeLatest(getType(evalActions.exportData), watchExport);
}